import { Component, input, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

type ImageDisplayAlign =
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'center'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';

@Component({
    selector: 'app-image-display',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './image-display.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './image-display.component.scss',
})
export class ImageDisplayComponent {
    // ────────────── Inputs ──────────────
    base64Image = input<string | null>(null);

    /** Fixed container width (e.g. "200px", "12rem"). */
    width = input<string | null>(null);

    /** Fixed container height (e.g. "200px", "12rem"). */
    height = input<string | null>(null);

    /**
     * true: image scales proportionally to fit container (scale down or up)
     * false: image uses natural size unless it exceeds the container (then it is scaled down)
     */
    scaleImageToCell = input<boolean>(false);

    /** true: container wraps the (possibly scaled) image tightly */
    shrinkCellToFit = input<boolean>(false);

    /** true => container display is block; false => inline-flex */
    blockMode = input<boolean>(false);

    align = input<ImageDisplayAlign>('center');

    altText = input<string>('Image');
    showPlaceholder = input<boolean>(true);
    placeholderText = input<string>('No image available');

    // ────────────── State ──────────────
    imageLoaded = signal(false);
    imageError = signal(false);
    naturalDimensions = signal<{ width: number; height: number } | null>(null);

    hasImage = computed(() => !!this.base64Image() && !this.imageError());

    shouldShowPlaceholder = computed(() => {
        const hasImageData = !!this.base64Image();
        const showPlaceholderFlag = this.showPlaceholder();
        const hasError = this.imageError();
        return !hasImageData && showPlaceholderFlag && !hasError;
    });

    alignmentClass = computed(() => `align-${this.align()}`);

    displayClass = computed(() => (this.blockMode() ? 'mode-block' : 'mode-inline-flex'));

    // ────────────── Size Parsing ──────────────

    private parseSizeToPx(value: string | null): number | null {
        if (!value) return null;

        const trimmed = value;

        if (trimmed.endsWith('px')) {
            return parseFloat(trimmed);
        }

        if (trimmed.endsWith('rem')) {
            const rem = parseFloat(trimmed);
            const rootFontSize = parseFloat(
                getComputedStyle(document.documentElement).fontSize
            );
            return rem * rootFontSize;
        }

        // fallback: assume px if numeric string
        const num = parseFloat(trimmed);
        return isNaN(num) ? null : num;
    }

    private widthPx = computed(() => this.parseSizeToPx(this.width()));
    private heightPx = computed(() => this.parseSizeToPx(this.height()));

    // ────────────── Scaling Logic ──────────────

    private scaleFactor = computed(() => {
        const natural = this.naturalDimensions();
        if (!natural) return 1;

        const w = this.widthPx();
        const h = this.heightPx();

        if (w == null && h == null) return 1;

        const ratioW =
            w != null ? w / natural.width : Number.POSITIVE_INFINITY;
        const ratioH =
            h != null ? h / natural.height : Number.POSITIVE_INFINITY;

        const fit = Math.min(ratioW, ratioH);

        return this.scaleImageToCell() ? fit : Math.min(1, fit);
    });

    imageRenderWidthPx = computed<number | null>(() => {
        const natural = this.naturalDimensions();
        if (!natural) return null;
        return Math.round(natural.width * this.scaleFactor());
    });

    imageRenderHeightPx = computed<number | null>(() => {
        const natural = this.naturalDimensions();
        if (!natural) return null;
        return Math.round(natural.height * this.scaleFactor());
    });

    containerWidthPx = computed<number | null>(() => {
        if (this.shrinkCellToFit()) return this.imageRenderWidthPx();
        return this.widthPx();
    });

    containerHeightPx = computed<number | null>(() => {
        if (this.shrinkCellToFit()) return this.imageRenderHeightPx();
        return this.heightPx();
    });

    // ────────────── Events ──────────────

    onImageLoad(event: Event): void {
        const img = event.target as HTMLImageElement;

        this.naturalDimensions.set({
            width: img.naturalWidth,
            height: img.naturalHeight,
        });

        this.imageLoaded.set(true);
        this.imageError.set(false);
    }

    onImageError(): void {
        this.imageError.set(true);
        this.imageLoaded.set(false);
        this.naturalDimensions.set(null);
    }

    onImageLoadStart(): void {
        this.imageLoaded.set(false);
        this.imageError.set(false);
        this.naturalDimensions.set(null);
    }
}
